import React, { useEffect, useState } from "react";
import { getBusinesses, saveBusiness } from "../../components/Api/GmsBusiness";
import { getCompanies } from "../../components/Api/GmsCompany";
import Select from "react-select";
import { Button } from "reactstrap";
import Swal from "sweetalert2";
import { Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import 'bootstrap/dist/css/bootstrap.min.css';
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

const defaultForm = {
  business_Id: null,
  business_Code: "",
  business_NameEn: "",
  business_NameAr: "",
  business_Company_Id: "",
  business_Active: true,
  business_TRN: "", // New field for TRN
  business_CreatedDate: new Date().toISOString(),
  business_CreatedBy: Number(localStorage.getItem("userId")) || 1,
  business_ModifiedDate: new Date().toISOString(),
  business_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
};

const emptyFilter = {
  business_Code: "",
  business_NameEn: "",
  business_NameAr: "",
  business_Company_Id: null,
  business_Active: null,
};

export default function BusinessPage() {
  const [businesses, setBusinesses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [showFilters, setShowFilters] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadBusinesses();
      loadCompanies();
    }
  }, [permissionsLoading, canView, page]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsBusiness');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsBusiness');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsBusiness');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await getCompanies({});
      setCompanies(data);
    } catch (error) {
      console.error("Error loading companies:", error);
    }
  };

  const loadBusinesses = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getBusinesses(filterData);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = data.slice(startIndex, endIndex);
      setBusinesses(paginatedData);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load companies: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        business_Active: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleCompanyChange = (selectedOption) => {
    setForm({
      ...form,
      business_Company_Id: selectedOption ? selectedOption.value : "",
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value === "" ? null : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = {
        ...form,
        business_ModifiedDate: new Date().toISOString(),
      };
      const result = await saveBusiness(formData);
      Swal.fire("Success", result.message, "success");
      setForm(defaultForm);
      setIsEditing(false);
      loadBusinesses(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save company: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (business) => {
    setViewLoadingRow(business.business_Id);
    try {
      const companyName = companies.find(c => c.company_Id === business.business_Company_Id)?.company_NameEn || business.business_Company_Id;
      
      Swal.fire({
        title: business.business_NameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Code:</strong> ${business.business_Code}</p>
              <p><strong>Name (English):</strong> ${business.business_NameEn}</p>
              <p><strong>Name (Arabic):</strong> ${business.business_NameAr}</p>
              <p><strong>Company:</strong> ${companyName}</p>
              <p><strong>TRN:</strong> ${business.business_TRN || 'N/A'}</p>
              <p><strong>Status:</strong> ${business.business_Active ? "Active" : "Inactive"}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
              Swal.fire("Error", "Failed to view company details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (business) => {
    setForm({
      ...business,
      business_ModifiedDate: new Date().toISOString(),
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadBusinesses(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadBusinesses(emptyFilter);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  // Generate options for searchable dropdowns
  const businessCodeOptions = businesses.map(b => ({
    value: b.business_Code,
    label: b.business_Code
  }));

  const businessNameEnOptions = businesses.map(b => ({
    value: b.business_NameEn,
    label: b.business_NameEn
  }));

  const businessNameArOptions = businesses.map(b => ({
    value: b.business_NameAr,
    label: b.business_NameAr
  }));

  const companyIdOptions = [...new Set(businesses.map(b => b.business_Company_Id))].map(id => ({
    value: id,
    label: id?.toString()
  }));

  const handleSelectChange = (selectedOption, field) => {
    setFilter({ 
      ...filter, 
      [field]: selectedOption ? selectedOption.value : null 
    });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <ClipLoader size={50} color="#556ee6" />
              <p className="mt-3">Loading permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 className="mb-0">Company Management</h4>
        </div>

        {/* Form Section - Only show if user has edit permission */}
        {canEdit && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                                {isEditing ? "Edit Company" : "Company Details"}
              </h5>
            </div>
            <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Name (English)</label>
                  <input
                    className="form-control"
                    name="business_NameEn"
                    placeholder="Name in English"
                    value={form.business_NameEn}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Name (Arabic)</label>
                  <input
                    className="form-control"
                    name="business_NameAr"
                    placeholder="Name in Arabic"
                    value={form.business_NameAr}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Group</label>
                  <Select
                    value={companies.find(c => c.company_Id === form.business_Company_Id) 
                      ? { value: form.business_Company_Id, label: companies.find(c => c.company_Id === form.business_Company_Id).company_NameEn }
                      : null}
                    onChange={handleCompanyChange}
                    options={companies.map(company => ({
                      value: company.company_Id,
                      label: company.company_NameEn
                    }))}
                    isClearable
                    placeholder="Select Group..."
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">TRN</label>
                  <input
                    className="form-control"
                    name="business_TRN"
                    placeholder="Enter TRN (Tax Registration Number)"
                    value={form.business_TRN}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <div className="form-check form-switch mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="business_Active"
                      checked={form.business_Active}
                      onChange={handleFormChange}
                      disabled={!canDelete} // Only allow status change if user has delete permission
                    />
                    <label className="form-check-label">{form.business_Active ? "Active" : "Inactive"}</label>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button 
                  className="btn btn-success me-2" 
                  type="submit" 
                  disabled={loading}
                >
                  <i className="bx bx-check-circle me-2"></i>
                  {loading ? "Saving..." : (isEditing ? "Update Company" : "Save Company")}
                </button>
                {isEditing && (
                  <button 
                    className="btn btn-danger" 
                    type="button" 
                    onClick={handleCancelEdit}
                  >
                    <i className="bx bx-x-circle me-2"></i>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
        )}

        {/* Filter Section */}
        <div className="card mb-4">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Filter Companies</h5>
              <button
                className="btn btn-primary d-flex align-items-center"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bx bx-filter me-2"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>
          {showFilters && (
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <label className="form-label">Business Code</label>
                  <Select
                    value={businessCodeOptions.find(option => option.value === filter.business_Code)}
                    onChange={(option) => handleSelectChange(option, 'business_Code')}
                    options={businessCodeOptions}
                    isClearable
                    placeholder="Select Business Code"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Name (English)</label>
                  <Select
                    value={businessNameEnOptions.find(option => option.value === filter.business_NameEn)}
                    onChange={(option) => handleSelectChange(option, 'business_NameEn')}
                    options={businessNameEnOptions}
                    isClearable
                    placeholder="Select Name (English)"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Name (Arabic)</label>
                  <Select
                    value={businessNameArOptions.find(option => option.value === filter.business_NameAr)}
                    onChange={(option) => handleSelectChange(option, 'business_NameAr')}
                    options={businessNameArOptions}
                    isClearable
                    placeholder="Select Name (Arabic)"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Group ID</label>
                  <Select
                    value={companyIdOptions.find(option => option.value === filter.business_Company_Id)}
                    onChange={(option) => handleSelectChange(option, 'business_Company_Id')}
                    options={companyIdOptions}
                    isClearable
                    placeholder="Select Group ID"
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button 
                  className="btn btn-primary me-2" 
                  onClick={handleApplyFilter} 
                  disabled={loading}
                >
                  <i className="bx bx-search me-2"></i>
                  Apply Filter
                </button>
                <button 
                  className="btn btn-warning" 
                  onClick={handleClearFilter}
                >
                  <i className="bx bx-refresh me-2"></i>
                  Clear Filter
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Section */}
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Company List ({businesses.length})</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Business Code</th>
                      <th>Name EN</th>
                      <th>Name AR</th>
                      <th>Group</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">No companies found</td>
                      </tr>
                    ) : (
                      businesses.map((b, index) => (
                        <tr key={b.business_Id}>
                          <td>{(page - 1) * pageSize + index + 1}</td>
                          <td>{b.business_Code}</td>
                          <td>{b.business_NameEn}</td>
                          <td>{b.business_NameAr}</td>
                          <td>{companies.find(c => c.company_Id === b.business_Company_Id)?.company_NameEn || b.business_Company_Id}</td>
                          <td>
                            <span className={`badge ${b.business_Active ? 'bg-success' : 'bg-danger'}`}>
                              {b.business_Active ? "Yes" : "No"}
                            </span>
                          </td>

                          <td>
                            <div className="d-flex gap-2">
                              {canEdit && (
                                <button
                                  className="btn btn-info btn-sm"
                                  onClick={() => handleEdit(b)}
                                >
                                  <i className="bx bx-edit me-1"></i>
                                  Edit
                                </button>
                              )}
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleView(b)}
                                disabled={viewLoadingRow === b.business_Id}
                              >
                                {viewLoadingRow === b.business_Id ? (
                                  <ClipLoader size={16} color="white" />
                                ) : (
                                  <>
                                    <Eye size={16} color="white" />
                                    <span className="ms-1">View</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                 style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
              <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
              </div>
              {totalCount > pageSize && (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                      color: page === 1 ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-left"></i>
                  </Button>
                  
                  {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                    const pageNum = index + 1;
                    const isActive = page === pageNum;
                    
                    return (
                      <Button
                        key={pageNum}
                        color={isActive ? "primary" : "light"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? '#556ee6' : '#fff',
                          borderColor: isActive ? '#556ee6' : '#dee2e6',
                          color: isActive ? '#fff' : '#495057',
                          fontWeight: isActive ? '600' : '400',
                          boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === Math.ceil(totalCount / pageSize)}
                    onClick={() => handlePageChange(page + 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                      color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}